import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { closeDialog } from '@shared/store/dialog.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import { Tag } from '@shared/models/tags.models';
import { UserInfo } from '@shared/models/user.models';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { LoadingOverlay } from '@shared/ui/molecules/LoadingOverlay';
import { createUserTagRequest, removeUserTagRequest } from '@shared/services/Tags';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { loadAllUserTags } from '@features/settings/store/userManagement.duck';
import { loadConfig } from '@shared/store/config.duck';
import { redirectTo } from '@shared/store/auth.duck';

interface Props {
  payload?: {
    user: UserInfo;
    currentWorkGroupIds: number[];
  }
}

export default function AddUserToWorkGroupsDialog(props: Props) {
  const dispatch = useDispatch();
  const workGroups = useSelector(selectAllTags);
  const currentWorkGroupIds = useMemo(() => props.payload?.currentWorkGroupIds ?? [], [props.payload]);
  const [selectedIds, setSelectedIds] = useState<number[]>(currentWorkGroupIds);
  const [loading, setLoading] = useState(false);

  const hasChanges = useMemo(
    () => selectedIds.length !== currentWorkGroupIds.length ||
      selectedIds.some(id => !currentWorkGroupIds.includes(id)),
    [selectedIds, currentWorkGroupIds]
  );

  const close = () => dispatch(closeDialog());

  const toggle = (workGroupId: number) => {
    setSelectedIds(prev => prev.includes(workGroupId)
      ? prev.filter(id => id !== workGroupId)
      : [...prev, workGroupId]);
  }

  const save = async () => {
    if (!props.payload?.user || !hasChanges) return;
    const userId = props.payload.user.userId;
    const toAdd = selectedIds.filter(id => !currentWorkGroupIds.includes(id));
    const toRemove = currentWorkGroupIds.filter(id => !selectedIds.includes(id));
    setLoading(true);
    try {
      for (const workGroupId of toAdd) {
        await createUserTagRequest(userId, workGroupId);
      }
      for (const workGroupId of toRemove) {
        await removeUserTagRequest(userId, workGroupId);
      }
      dispatch(loadAllUserTags());
      dispatch(loadConfig(['workGroups']));
      dispatch(successNotification('Workgroups updated'));
      close();
    } catch {
      dispatch(failureNotification('Failed to update workgroups'));
      setLoading(false);
    }
  }

  const goToWorkGroups = () => {
    close();
    dispatch(redirectTo('/manage/teams-and-users?panel=workgroups'));
  }

  if (workGroups.length === 0) {
    return (
      <Dialog isOpen={true} onClose={close} showLogo={true}>
        <DialogHeader title="manage-users-page.assign-to-workgroup" />
        <BodyRegular style={{textAlign: 'center', marginBottom: 32}}>
          No workgroups have been created yet.
        </BodyRegular>
        <PrimaryButton fullWidth={true}
                       click={goToWorkGroups}
                       text="button.add-new-tag" />
      </Dialog>
    )
  }

  return (
    <Dialog isOpen={true} onClose={close} showLogo={true}>
      <DialogHeader title="manage-users-page.assign-to-workgroup" />
      {loading && <LoadingOverlay />}

      <WorkGroupList>
        {workGroups.map((workGroup: Tag) => (
          <WorkGroupRow key={workGroup.id}>
            <BodyRegular weight={600}>{workGroup.label}</BodyRegular>
            <Checkbox value={selectedIds.includes(workGroup.id)} onChange={() => toggle(workGroup.id)} />
          </WorkGroupRow>
        ))}
      </WorkGroupList>

      <PrimaryButton fullWidth={true}
                     disabled={!hasChanges}
                     click={save}
                     text="button.save-changes" />
    </Dialog>
  )
}

const WorkGroupList = styled.div`
  margin-bottom: 32px;
  max-height: 300px;
  overflow-y: auto;
`

const WorkGroupRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
`
